/**
 * ScriptIntegrityReporter - PCI 6.4.3
 *
 * Detects scripts loaded in runtime, calculates SHA-256 when possible
 * (inline and external same-origin) and reports to /api/adr/script-integrity.
 *
 */
import Logger from '../core/logger.js';
import HashCalculator from './hash-calculator.js';
import ScriptCache from './script-cache.js';
import RateLimiter from './rate-limiter.js';
import IntegrityClient from './integrity-client.js';

export class ScriptIntegrityReporter {
	constructor(config, options = {}) {
		this.config = config;
		this.logger = new Logger(config);
		this.hash = new HashCalculator();
		this.cache = new ScriptCache(this.config, this.logger);
		this.rateLimiter = new RateLimiter(config?.get?.('scriptIntegrityMaxReportsPerMin'));
		this.client = new IntegrityClient(config, this.logger, {
			tokenManager: options.tokenManager || null,
			sensorIntegrity: options.sensorIntegrity || null
		});

		this.observer = null;
		this.debounceMs = Number(config?.get?.('scriptIntegrityDebounceMs')) || 500;
		this._debounceTimer = null;
	}

	start() {
		if (typeof document === 'undefined') return;
		this.scanInitialScripts();
		this.setupMutationObserver();
	}

	stop() {
		if (this.observer) {
			try { this.observer.disconnect(); } catch {}
			this.observer = null;
		}
		if (this._debounceTimer) {
			clearTimeout(this._debounceTimer);
			this._debounceTimer = null;
		}
	}

	scanInitialScripts() {
		const scripts = this.collectScripts();
		for (const s of scripts) {
			this.processScript(s);
		}
	}

	collectScripts() {
		if (typeof document === 'undefined') return [];
		const external = Array.from(document.querySelectorAll('script[src]'));
		const inline = Array.from(document.querySelectorAll('script:not([src])'));
		return external.concat(inline);
	}

	setupMutationObserver() {
		if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;

		const scheduleScan = () => {
			if (this._debounceTimer) clearTimeout(this._debounceTimer);
			this._debounceTimer = setTimeout(() => {
				this._debounceTimer = null;
				this.scanInitialScripts();
			}, this.debounceMs);
		};

		this.observer = new MutationObserver((mutations) => {
			for (const m of mutations) {
				if (m.type !== 'childList' || !m.addedNodes) continue;
				for (const node of m.addedNodes) {
					if (!node || node.nodeType !== 1) continue;
					if (node.tagName === 'SCRIPT') {
						this.processScript(node);
					} else if (node.querySelectorAll) {
						const nested = node.querySelectorAll('script');
						if (nested && nested.length) scheduleScan();
					}
				}
			}
		});

		try {
			this.observer.observe(document.documentElement || document, { childList: true, subtree: true });
		} catch {}
	}

	async processScript(scriptEl) {
		if (!scriptEl || scriptEl.nodeType !== 1) return;
		if (this.cache.isProcessed(scriptEl)) return;
		
		if (scriptEl.src) {
			const src = scriptEl.src.toLowerCase();
			if (src.indexOf('adr-sensor') !== -1 || src.indexOf('iris-adr') !== -1) {
				this.cache.markProcessed(scriptEl);
				return;
			}
		}
		
		this.cache.markProcessed(scriptEl);

		const isExternal = !!scriptEl.src;

		if (isExternal) {
			const isThirdParty = this.hash.detectThirdParty(scriptEl.src);
			const normalizedUrlForSend = isThirdParty
				? this.hash.normalizeScriptUrlWithOrigin(scriptEl.src)
				: this.hash.normalizeScriptUrl(scriptEl.src);
			const scriptHash = await this.hash.sha256HexFromUrlSameOrigin(scriptEl.src);

			if (!scriptHash) {
				if (isThirdParty) {
					await this.maybeReport(normalizedUrlForSend, null, {
						source: 'third_party',
						can_hash: false,
						hash_status: 'not_applicable'
					});
				} else {
					this.logger.debug('Script integrity skip (no hash, same-origin)', normalizedUrlForSend);
				}
				return;
			}

			await this.maybeReport(normalizedUrlForSend, scriptHash, {
				source: isThirdParty ? 'third_party' : 'first_party',
				can_hash: true,
				hash_status: undefined
			});
			return;
		}

		const rawContent = scriptEl.textContent || '';
		const content = this.hash.normalizeInlineContent(rawContent);
		const scriptHash = await this.hash.sha256Hex(content);
		if (!scriptHash) return;

		// Inline id as-is; not passed through normalizeScriptUrl / normalizeScriptUrlWithOrigin.
		const inlineId = `inline/${scriptHash}`;
		await this.maybeReport(inlineId, scriptHash, { source: 'first_party', can_hash: true });
	}

	async maybeReport(scriptUrl, scriptHash, options = {}) {
		const isThirdParty = options.source === 'third_party';
		if (!scriptUrl) return;
		if (!isThirdParty && !scriptHash) return;

		const cacheKey = scriptHash ?? '';
		if (this.cache.hasReported(scriptUrl, cacheKey) || this.cache.isInFlight(scriptUrl, cacheKey)) return;
		if (!this.rateLimiter.canReport()) {
			this.logger.warn('Script integrity rate limit exceeded');
			return;
		}

		this.cache.markInFlight(scriptUrl, cacheKey);
		this.rateLimiter.record();

		try {
			const payload = {
				script_url: scriptUrl,
				script_hash: scriptHash ?? null,
				normalized_script_url: scriptUrl,
				source: options.source ?? 'first_party',
				can_hash: options.can_hash ?? (scriptHash != null),
				hash_status: options.hash_status ?? (scriptHash ? undefined : 'not_applicable')
			};
			const res = await this.client.report(payload);
			this.cache.markReported(scriptUrl, cacheKey);

			if (res && res.valid === false) {
				this.logger.warn('Script integrity violation', {
					script_url: scriptUrl,
					violation_type: res.violation_type,
					event_id: res.event_id
				});
			} else {
				this.logger.debug('Script integrity validated', { script_url: scriptUrl });
			}
		} finally {
			this.cache.clearInFlight(scriptUrl, cacheKey);
		}
	}
}

export default ScriptIntegrityReporter;

